import { BriefResponseDto, BriefStatusEnum } from '../../../../common/general-types/briefs.dto';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';

/**
 * How long each video should be. Piece briefs carry it on every piece; their price row
 * stores every duration rate, so reading the rates (the legacy rule) always says 15s.
 */
export function briefDurationLabel(brief: BriefResponseDto | undefined): string {
  const seconds = [...new Set((brief?.pieces ?? []).map((piece) => piece.durationSeconds).filter((s) => s > 0))].sort(
    (a, b) => a - b,
  );
  if (seconds.length === 1) {
    return `${seconds[0]}s`;
  }
  if (seconds.length > 1) {
    return `${seconds[0]}s – ${seconds[seconds.length - 1]}s`;
  }
  const price = brief?.currentPrice;
  if (!price) {
    return 'Unknown';
  }
  if (price.fifteenSecPrice > 0) return 'up to 15s';
  if (price.thirtySecPrice > 0) return '15s to 30s';
  if (price.sixtySecPrice > 0) return '30s to 60s';
  return 'Unknown';
}

const STATUS_LABELS: Partial<Record<BriefStatusEnum, string>> = {
  [BriefStatusEnum.Public]: 'Public',
  [BriefStatusEnum.Private]: 'Private',
  [BriefStatusEnum.Complete]: 'Complete',
  [BriefStatusEnum.Deleted]: 'Deleted',
  [BriefStatusEnum.Draft]: 'Draft',
};

export function briefStatusLabel(brief: BriefResponseDto | undefined): string {
  const status = brief?.currentStatus?.status;
  return status == null ? '–' : (STATUS_LABELS[status as BriefStatusEnum] ?? '–');
}

/** A creator's review rating, as the creator cards show it; null when they have no reviews yet. */
export function creatorReviewRating(creator: CreatorResponseDto): number | null {
  const reviews = Number(creator['total_reviews']) || 0;
  const average = Number(creator['average_rating']) || 0;
  return reviews > 0 && average > 0 ? average : null;
}

/** Mean review rating of the selected creators who have reviews; null when none do. */
export function averageReviewRating(creators: CreatorResponseDto[]): number | null {
  const ratings = creators.map(creatorReviewRating).filter((rating): rating is number => rating !== null);
  return ratings.length ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length : null;
}
